import { GridComponent } from '@vaad1st/component.layout.grid';
import { GlobalAlertBannerComponent } from '@vaad1st/component.molecule.global-alert-banner';
import dynamic from 'next/dynamic';
import ContentCardContainer from 'src/components/content-card-container/content-card-container.component';
import CountdownTimerComponent from 'src/components/countdown-timer/countdown-timer.component';
import HeadingComponent from 'src/components/heading-component/heading-component.component';
import Section from 'src/components/layout-components/home-page-section.component';
import SpacerComponent from 'src/components/layout-components/spacer.component';
import CarouselComponent from '../carousel/carousel.component';
import ContentCardComponent from '../content-card/content-card.component';
import GridItem, {
  getGridItemContentClassName,
  gridItemBoundedClassName,
} from '../grid-item/grid-item.component';
import IconPanelComponent from '../icon-panel/icon-panel.component';
import PromoBanner from '../promo-banner/promo-banner.component';
import SearchPanelComponent from '../search-panel/search-panel.component';
import TabsComponent from '../tabs/tabs-component';
import TextLockupComponent from '../text-lockup/text-lockup.component';

const AccordionComponent = dynamic(
  () => import('../accordion/accordion-component'),
);
const ArticleListContainer = dynamic(
  () => import('../article-list-container/article-list-container.component'),
);
const Breadcrumbs = dynamic(
  () => import('../breadcrumbs/breadcrumbs.component'),
);
const ClimateChartComponent = dynamic(
  () => import('../climate-chart/climate-chart.component'),
);
const DestinationCarouselComponent = dynamic(() =>
  import('../destination-carousel/destination-carousel.component').then(
    mod => mod.DestinationCarouselComponent,
  ),
);
const FlyingDaysComponent = dynamic(
  () => import('../flying-days/flying-days.component'),
);
const HeroBanner = dynamic(
  () => import('../hero-banner/hero-banner.component'),
);
const HotelsCarouselComponent = dynamic(
  () => import('../hotels-carousel/hotels-carousel.component'),
);
const HotelsMapComponent = dynamic(
  () => import('../hotels-map/hotels-map.component'),
);
const ImageComponent = dynamic(() => import('../image/image.component'));
const LinksBanner = dynamic(
  () => import('../link-banner/link-banner.component'),
);
const PartnerCodesComponent = dynamic(
  () => import('../partner-codes/partner-codes.component'),
);
const RouteMapComponent = dynamic(
  () => import('../route-map/route-map.component'),
);
const SlideShowComponent = dynamic(
  () => import('../slide-show/slide-show.component'),
);
const StoreFinderComponent = dynamic(
  () => import('../store-finder/store-finder.component'),
);
const TableCard = dynamic(() => import('../table-card/table-card.component'));
const TailfinHeroBannerTwoComponent = dynamic(() =>
  import('../tailfin-hero-banner-two/tailfin-hero-banner-two.component').then(
    mod => mod.TailfinHeroBannerTwoComponent,
  ),
);
const TailfinHeroBannerComponent = dynamic(() =>
  import('../tailfin-hero-banner/tailfin-hero-banner.component').then(
    mod => mod.TailfinHeroBannerComponent,
  ),
);
const TextComponent = dynamic(() => import('../text-component/text.component'));
const VideoComponent = dynamic(
  () => import('../video-component/video-component.component'),
);

export default function PageRenderer({ items }: { items: any }) {
  if (!items || items.length === 0 || !items[0].components) return null;

  return (
    <GridComponent as="section">
      {items[0].components.map((item: any, index: any) => {
        const { C, fullWidth } = getComponent(item.componentType);
        const HOMEPAGE_SEARCH_PANEL = 'homepage-search-panel';
        const isHomepageSearchPanel =
          (item.componentProps &&
            item.componentProps.componentId === HOMEPAGE_SEARCH_PANEL) ||
          false;

        if (!C) return null;
        const isBounded = !fullWidth;
        const contentClassName = getGridItemContentClassName({
          bounded: false,
        });

        return (
          <GridItem
            key={`grid-item--${index}`}
            span={12}
            className={isBounded ? gridItemBoundedClassName : undefined}
            {...(isHomepageSearchPanel && {
              gridRow: { mobile: 1, desktop: 'auto' },
            })}>
            <div className={contentClassName}>
              <C key={`item--${index}`} {...item.componentProps} />
            </div>
          </GridItem>
        );
      })}
    </GridComponent>
  );
}

export const getComponent = (
  componentType: string,
): { C: any; fullWidth: boolean } => {
  switch (componentType) {
    case 'accordion':
      return { C: AccordionComponent, fullWidth: false };
    case 'articleslist':
      return { C: ArticleListContainer, fullWidth: true };
    case 'breadcrumbs':
      return { C: Breadcrumbs, fullWidth: false };
    case 'carousel':
      return { C: CarouselComponent, fullWidth: true };
    case 'climate-chart':
      return { C: ClimateChartComponent, fullWidth: false };
    case 'container':
      return { C: Section, fullWidth: true };
    case 'content-card':
      return { C: ContentCardComponent, fullWidth: false };
    case 'content-card-container':
      return { C: ContentCardContainer, fullWidth: false };
    case 'countdown-timer':
      return { C: CountdownTimerComponent, fullWidth: true };
    case 'destination-carousel':
      return { C: DestinationCarouselComponent, fullWidth: true };
    case 'global-alert-banner':
      return { C: GlobalAlertBannerComponent, fullWidth: true };
    case 'heading-component':
      return { C: HeadingComponent, fullWidth: true };
    case 'herobanner':
      return { C: HeroBanner, fullWidth: true };
    case 'hotels-carousel':
      return { C: HotelsCarouselComponent, fullWidth: true };
    case 'hotels-map':
      return { C: HotelsMapComponent, fullWidth: false };
    case 'icon-panel':
      return { C: IconPanelComponent, fullWidth: true };
    case 'image':
      return { C: ImageComponent, fullWidth: false };
    case 'affinity-promo-codes':
      return { C: PartnerCodesComponent, fullWidth: true };
    case 'link-banner':
      return { C: LinksBanner, fullWidth: false };
    case 'promobanner':
      return { C: PromoBanner, fullWidth: true };
    case 'route-map':
      return { C: RouteMapComponent, fullWidth: false };
    case 'search-panel':
      return { C: SearchPanelComponent, fullWidth: true };
    case 'slideshow-banner':
      return { C: SlideShowComponent, fullWidth: true };
    case 'spacer-component':
      return { C: SpacerComponent, fullWidth: true };
    case 'table-card':
      return { C: TableCard, fullWidth: false };
    case 'tabs':
      return { C: TabsComponent, fullWidth: false };
    case 'tailfin-hero-banner':
      return { C: TailfinHeroBannerComponent, fullWidth: true };
    case 'tailfin-hero-banner-two':
      return { C: TailfinHeroBannerTwoComponent, fullWidth: true };
    case 'text-lockup-component':
      return { C: TextLockupComponent, fullWidth: false };
    case 'text-component':
      return { C: TextComponent, fullWidth: false };
    case 'video-component':
      return { C: VideoComponent, fullWidth: false };
    case 'flying-days':
      return { C: FlyingDaysComponent, fullWidth: false };
    case 'store-finder':
      return { C: StoreFinderComponent, fullWidth: false };
    default:
      return { C: null, fullWidth: false };
  }
};
